import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import { useRegistrants } from 'volunteering/volunteers/hooks'
import Loading from 'components/Loading'
import { DayNavigationBar } from '../components/DayNavigationBar'
import PageHeader from 'App/components/PageHeader'
import { useGetSearchableContactsFromVolunteersArray } from 'messages/hooks/useGetSearchableContactsFromVolunteersArray'
import { RegistrantsTable } from 'registrant/components/RegistrantsTable'
import { makeStyles } from '@material-ui/core/styles'
import { useTimeZone } from 'civic-champs-shared/auth/hooks'
import dayjs from 'utils/dayjs'

const tableName = 'registrants'

const useStyles = makeStyles({
  container: {
    display: 'flex',
  },
})

const getDateFromSearch = (search: string, timeZone: string): Date => {
  const dateParam = new URLSearchParams(search).get('date')
  if (dateParam && dayjs(dateParam, 'YYYY-MM-DD', true).isValid()) {
    return dayjs.tz(dateParam, timeZone).toDate()
  }

  return new Date()
}

export const Registrants = (props: RouteComponentProps) => (
  <PageHeader title="Registrations" subTitle="Review and manage daily volunteer registrations">
    <RegistrantsComponent {...props} />
  </PageHeader>
)

export const RegistrantsComponent = ({ location, history }: RouteComponentProps) => {
  const timeZone = useTimeZone()
  const [date, setDate] = useState(() => getDateFromSearch(location.search, timeZone))

  useEffect(() => {
    setDate(prev => {
      const next = getDateFromSearch(location.search, timeZone)
      return next?.getTime() === prev?.getTime() ? prev : next
    })
  }, [location.search, timeZone])

  const handleSetDate = useCallback(
    (newDate: React.SetStateAction<Date>) => {
      setDate(prevDate => {
        const resolvedDate = typeof newDate === 'function' ? newDate(prevDate) : newDate
        const dateStr = dayjs(resolvedDate).tz(timeZone).format('YYYY-MM-DD')
        history.replace({ pathname: location.pathname, search: `date=${dateStr}` })
        return resolvedDate
      })
    },
    [history, location.pathname, timeZone],
  )

  useEffect(() => {
    if (!new URLSearchParams(location.search).get('date')) {
      const dateStr = dayjs(date).tz(timeZone).format('YYYY-MM-DD')
      history.replace({ pathname: location.pathname, search: `date=${dateStr}` })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [{ registrants, loading, initiallyLoaded, reload: reloadRegistrants }, { eagerRemoveMany }] = useRegistrants({
    date,
  } as any) as any

  const getSearchableContacts = useGetSearchableContactsFromVolunteersArray(registrants)

  const dateAlias = useMemo(() => {
    const today = dayjs().tz(timeZone)
    const tomorrow = today.add(1, 'day')
    const yesterday = today.subtract(1, 'day')
    const selectedDate = dayjs(date).tz(timeZone)
    if (selectedDate.isSame(today, 'day')) {
      return `Today`
    } else if (selectedDate.isSame(tomorrow, 'day')) {
      return `Tomorrow`
    } else if (selectedDate.isSame(yesterday, 'day')) {
      return `Yesterday`
    }
  }, [date, timeZone])

  const autocompleteSectionTitle = useMemo(() => {
    return `${dateAlias || dayjs(date).format('MM/DD/YYYY')}'s Registrants`
  }, [date, dateAlias])

  const classes = useStyles()

  return (
    <div className={classes.contaier}>
      <div>
        <DayNavigationBar date={date} setDate={handleSetDate} dateAlias={dateAlias} />
      </div>
      {loading && (
        <div>
          <Loading />
        </div>
      )}
      <div style={{ display: loading ? 'none' : 'block' }}>
        <RegistrantsTable
          {...{
            registrants,
            initiallyLoaded,
            tableName,
            getSearchableContacts,
            autocompleteSectionTitle,
            eagerRemoveMany,
            reloadRegistrants,
          }}
        />
      </div>
    </div>
  )
}
